import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { capitalize } from '@shared/utils/strings';
import {
  ActionEnum,
  BandwidthHistograms,
  CacheStatus,
  DateHistograms,
  ResponseTimeHistograms,
} from '@shared/services/logs/logs.service';
import { ANALYZED, BLOCKED, ROBOT, SUSPECT } from '@shared/components/highcharts/donut/donut';
import { DateRange } from '@shared/utils/date-range';
import Highcharts, { SeriesAreaOptions, SeriesColumnOptions, SeriesSplineOptions } from 'highcharts';

const legendYx = 7;
const timeIntervals = [
  0.001, 0.005, 0.01, 0.05, 0.1, 0.5, 1, 5, 10, 30, 60, 300, 600, 1800, 3600, 10800, 21600, 43200, 86400, 172800,
  604800,
];
const intervalsBarOptimum = 50;
export const areaOpacity = 0.5;

export function getBaseChart(): Highcharts.Options {
  return {
    chart: {
      animation: false,
      shadow: false,
      borderWidth: 0,

      borderRadius: 5,
      spacingRight: 20,
      spacingLeft: 20,
      zooming: {
        type: 'x',
        resetButton: {
          theme: {
            style: {
              display: 'none',
            },
          },
        },
      },
      style: {
        cursor: 'crosshair',
      },
    },
    title: null,
    noData: {
      style: {
        fontWeight: 'normal',
        fontSize: '24px',
        color: 'rgb(34, 34, 34)',
      },
    },
    accessibility: {
      enabled: false,
    },
    time: {
      timezone: undefined, // Use browser default timezone
    },
    xAxis: {
      type: 'datetime',
      currentDateIndicator: true,
    },
    legend: {
      enabled: true,
      itemStyle: {
        fontSize: '0.7em',
        textOverflow: 'ellipsis',
      },
    },
    plotOptions: {
      area: {
        fillOpacity: areaOpacity,
        pointPlacement: 'between',
      },
      series: {
        animation: false,
        connectNulls: false,
      },
      spline: {
        pointPlacement: 'between',
        pointInterval: 10,
      },
      line: {
        showInLegend: true,
        pointPlacement: 'between',
      },
      column: {
        showInLegend: true,
        stacking: 'normal',
        pointWidth: undefined,
        groupPadding: 0,
        pointPadding: 0.3,
        pointPlacement: 'between',
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      chartOptions: {
        chart: {
          backgroundColor: '#FFFFFF',
        },
      },
      buttons: {
        contextButton: {
          x: 10,
          align: 'right',
          menuItems: [
            'printChart',
            'separator',
            'downloadPNG',
            'downloadJPEG',
            'downloadSVG',
            'separator',
            'downloadCSV',
            'downloadXLS',
          ],
        },
      },
    },
  };
}

export function computeInterval(periode: DateRange): number {
  const interval = Math.abs(periode.length('second'));
  return _.minBy(timeIntervals, (i) => Math.abs(intervalsBarOptimum - interval / i));
}

export const TRAFIC = 'traffic';
export const RESPONSE_TIME = 'responseTime';
export const RESPONSES_CODES = 'responsesCodes';
export const BANDWIDTH = 'bandwidth';

export const TOP_SITES_BY_TRAFFIC = 'topSitesByTraffic';
export const TOP_IP_BY_TRAFFIC = 'topTrafficByIP';
export const TOP_USER_AGENTS_BY_TRAFFIC = 'topUserAgentsByTraffic';
export const TOP_URLS_BY_RESPONSE_TIME = 'topUrlsByResponseTime';
export const TOP_URLS_RESPONSE_TIME_SUM = 'topUrlsByResponseTimeSum';
export const TOP_URLS_BY_BANDWIDTH = 'topUrlsByBandwidth';
export const TOP_SITES_BY_BANDWIDTH = 'topSitesByBandwidth';
export const TOP_URLS_BY_ERROR_CODES = 'topUrlsByErrorCodes';

export const GraphTypes = [TRAFIC, RESPONSE_TIME, RESPONSES_CODES, BANDWIDTH] as const;

export type GraphType = (typeof GraphTypes)[number];

export const BreakdownTypes = [
  TOP_SITES_BY_TRAFFIC,
  TOP_URLS_BY_RESPONSE_TIME,
  TOP_URLS_RESPONSE_TIME_SUM,
  TOP_URLS_BY_ERROR_CODES,
  TOP_URLS_BY_BANDWIDTH,
  TOP_SITES_BY_BANDWIDTH,
  TOP_USER_AGENTS_BY_TRAFFIC,
  TOP_IP_BY_TRAFFIC,
] as const;
export type BreakdownType = (typeof BreakdownTypes)[number];

export const TrafficHistogramTypes = ['traffic', BLOCKED, ROBOT, SUSPECT, ANALYZED] as const;
export type TrafficHistogramType = (typeof TrafficHistogramTypes)[number];

export const BreakdownsByGraphType: Record<GraphType, BreakdownType[]> = {
  traffic: [TOP_SITES_BY_TRAFFIC, TOP_USER_AGENTS_BY_TRAFFIC, TOP_IP_BY_TRAFFIC],
  responseTime: [TOP_URLS_BY_RESPONSE_TIME, TOP_URLS_RESPONSE_TIME_SUM],
  responsesCodes: [TOP_URLS_BY_ERROR_CODES],
  bandwidth: [TOP_SITES_BY_BANDWIDTH, TOP_URLS_BY_BANDWIDTH],
};

@Injectable()
export class Graph {
  constructor(private translate: TranslateService) {}

  formatGraphData(type: GraphType, data: DateHistograms<any, any> | any) {
    return this.formatData[type](data);
  }

  private readonly formatData: Record<GraphType,(any)=>Highcharts.Options> = {
    traffic: (histograms: DateHistograms<TrafficHistogramType, any>) =>
      this.formatDataTrafic(histograms),
    responseTime: (histograms: ResponseTimeHistograms) =>
      this.formatDataResponseTime(histograms),
    responsesCodes: (histograms: DateHistograms<TrafficHistogramType, any>) =>
      this.formatDataResponsesCodes(histograms),
    bandwidth: (histograms: BandwidthHistograms) =>
      this.formatDataBandwidth(histograms),
  };

  formatDataTrafic(histograms: DateHistograms<TrafficHistogramType, any>): Highcharts.Options {
    return _.merge(getBaseChart(), {
      tooltip: {
        valueSuffix: `&nbsp${this.translate.instant('requests').charAt(0).toLowerCase()}${this.translate.instant('requests').substring(1)}`,
      },
      yAxis: [
        {
          allowDecimals: false,
          title: {
            text: this.translate.instant(`Graph.seriesName.${TRAFIC}`),
            x: legendYx * -1,
          },
        },
        {
          allowDecimals: false,
          opposite: true,
          title: {
            text: this.translate.instant(`Graph.seriesName.${BLOCKED}`),
            x: legendYx,
          },
          labels: {
            style: {
              color: '#FF5370',
            },
          },
        },
      ],
      series: [
        {
          id: TRAFIC,
          zIndex: 1,
          yAxis: 0,
          color: '#c3c3c3',
          type: 'area',
          name: this.translate.instant(`Graph.seriesName.${TRAFIC}`),
          data: sortTimeSeries(histograms.traffic.data),
        },
        {
          id: BLOCKED,
          zIndex: 3,
          yAxis: 1,
          color: '#FF5370',
          type: 'column',
          name: this.translate.instant(`Graph.seriesName.${BLOCKED}`),
          data: sortTimeSeries(histograms.blocked.data),
          custom: {
            category: TRAFIC,
            slug: ActionEnum.BLOCKED,
          },
        },
        {
          id: ROBOT,
          zIndex: 3,
          yAxis: 0,
          color: '#fed330',
          type: 'line',
          name: this.translate.instant(`Graph.seriesName.${ROBOT}`),
          data: sortTimeSeries(histograms.robot.data),
        },
        {
          id: SUSPECT,
          zIndex: 3,
          yAxis: 1,
          color: '#f7a35c',
          type: 'column',
          name: this.translate.instant(`Graph.seriesName.${SUSPECT}`),
          data: sortTimeSeries(histograms.suspect.data),
          custom: {
            category: TRAFIC,
            slug: ActionEnum.SUSPICIOUS,
          },
        },
        {
          id: ANALYZED,
          zIndex: 4,
          yAxis: 0,
          color: '#3fdbbc',
          type: 'line',
          name: this.translate.instant(`Graph.seriesName.${ANALYZED}`),
          data: sortTimeSeries(histograms.analyzed.data),
        },
      ],
    });
  }

  formatDataResponseTime(histograms: ResponseTimeHistograms): Highcharts.Options {
    const translateKey = 'Percentile';
    return {
      ...getBaseChart(),
      yAxis: [
        {
          allowDecimals: false,
          title: {
            text: this.translate.instant('requests'),
            x: legendYx * -1,
          },
        },
        {
          crosshair: true,
          opposite: true,
          title: {
            text: this.translate.instant('ResponseTime') + ' (ms)',
            x: legendYx,
          },
        },
      ],
      series: [
        dummyColumnSeries(histograms.responseTime),
        {
          type: 'area',
          zIndex: 1,
          yAxis: 0,
          color: '#c3c3c3',
          name: this.translate.instant('requests'),
          data: sortTimeSeries(histograms.traffic.data),
          opacity: 0.5,
          tooltip: {
            valueSuffix: `&nbsp${capitalize(this.translate.instant('requests'))}`,
          },
        },
        ..._.toPairs(histograms.responseTime).map(
          ([name, histogram]) =>
            <SeriesSplineOptions>{
              type: 'spline',
              zIndex: 3,
              custom: { percentile: name, category: RESPONSE_TIME },
              name: this.translate.instant(translateKey, { p: name }),
              color: percentileColors[name],
              data: sortTimeSeries(histogram.data.map(([date, value]) => [date, Math.trunc(value)])),
              yAxis: 1,
              tooltip: {
                valueSuffix: '&nbsp;ms',
              },
            },
        ),
      ],
    };
  }

  formatDataBandwidth(data: BandwidthHistograms): Highcharts.Options {
    const translateKey = 'Percentile';
    return {
      ...getBaseChart(),
      yAxis: [
        {
          allowDecimals: false,
          title: {
            text: this.translate.instant('BandwidthReport.OutputSizeTitle'),
            x: legendYx * -1,
          },
        },
        {
          crosshair: true,
          opposite: true,
          title: {
            text: this.translate.instant('BandwidthReport.ContentLengthTitle'),
            x: legendYx,
          },
        },
      ],
      series: [
        dummyColumnSeries(data.contentLength),
        ..._.toPairs(data.contentLength).map(
          ([name, histogram]) =>
            <SeriesSplineOptions>{
              type: 'spline',
              zIndex: 2,
              custom: { percentile: name, category: BANDWIDTH },
              name: this.translate.instant(translateKey, { p: name }),
              color: percentileColors[name],
              data: sortTimeSeries(histogram.data.map(([date, value]) => [date, Math.trunc(value)])),
              yAxis: 1,
              tooltip: {
                valueSuffix: '&nbsp;' + this.translate.instant('bytes'),
              },
            },
        ),
        ...cacheStatusOrder.map(
          (cacheStatus) =>
            <SeriesAreaOptions>{
              zIndex: 1,
              yAxis: 0,
              color: cacheStatusColors[cacheStatus],
              type: 'area',
              stacking: 'normal',
              name: this.translate.instant(`CacheStatus.${cacheStatus}`),
              data: sortTimeSeries(data.cacheStatus[cacheStatus].data),
              tooltip: {
                valueSuffix: ` ` + this.translate.instant('bytes'),
              },
              lineWidth: 0,
              marker: {
                enabled: false,
              },
            },
        ),
      ],
    };
  }

  formatDataResponsesCodes(histograms: DateHistograms<string, {}>): Highcharts.Options {
    const codesColorMap = {
      1: 'blue',
      2: '#2ed8b6',
      3: 'yellow',
      4: '#f7a35c',
      5: '#ff5370',
    };

    return _.merge(getBaseChart(), {
      plotOptions: {
        column: {
          pointPadding: 0.05,
        },
      },
      chart: {
        spacingRight: 60,
      },
      tooltip: {
        valueSuffix: `&nbsp${this.translate.instant('requests').charAt(0).toLowerCase()}${this.translate.instant('requests').substring(1)}`,
      },
      yAxis: {
        title: {
          text: this.translate.instant('requests'),
          x: legendYx * -1,
        },
      },
      series: _.map(
        histograms,
        (histogram, code) =>
          <SeriesColumnOptions>{
            name: code,
            color: codesColorMap[code.charAt(0)],
            custom: {
              category: RESPONSES_CODES,
              slug: code,
            },
            type: 'column',
            data: sortTimeSeries(histogram.data),
          },
      ),
      exporting: {
        buttons: {
          contextButton: {
            x: 40,
          },
        },
      },
    });
  }
}

function dummyColumnSeries(dateHistoGrams: DateHistograms<any, any>): SeriesColumnOptions {
  return {
    type: 'column',
    visible: false,
    data: sortTimeSeries(_.toPairs(dateHistoGrams)[0][1].data),
    showInLegend: false,
  };
}

function sortTimeSeries(data) {
  return _.sortBy(data, ([date]) => date);
}

const percentileColors = { '95': '#f94e3f', '75': '#f6b352', '50': '#efdc05' };
export const cacheStatusColors: { [key in CacheStatus]: string } = {
  NONE: '#c3c3c3',
  MISS: 'grey',
  HIT: 'green',
  HIT_CDN: 'lime',
};
const cacheStatusOrders: { [key in CacheStatus]: number } = {
  HIT_CDN: 0,
  HIT: 1,
  MISS: 2,
  NONE: 3,
};
export const cacheStatusOrder: CacheStatus[] = Object.keys(cacheStatusOrders)
  .map((x) => x as CacheStatus)
  .sort((a, b) => cacheStatusOrders[a] - cacheStatusOrders[b]);
